import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import {
  selectFinancialYear,
  selectPartnerId,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import { MeasureController } from 'core/analytics'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { ExtendedPartnerContacts } from 'partner360/features/contacts'
import { AssortmentTab } from 'partner360/widgets/assortmentTab/AssortmentTab'
import PartnerBasicInfo from 'partner360/widgets/partnerBasicInfoRedesigned/PartnerBasicInfo'
import { RamManagerAssignment } from 'partner360/widgets/ramManagerAssignment/RamManagerAssignment'

import CommitmentWrapper from '../../../CommitmentWrapper'
import { Planning } from '../../../planning/Planning'
import { setWizardIsLoading } from '../../features/planningWizard'
import { isWizardStepsGroup } from '../../helpers/isWizardStepsGroup'
import { useNotApplicablePlanningStep } from '../../hooks/useNotApplicablePlanningStep'
import { IWizardStep, IWizardStepsGroup } from '../../models/IWizardStep'
import { NotApplicableStep } from '../notApplicableCategory/NotApplicableStep'

import { defaultFinSobItem } from './config'

import styles from './StepRenderer.module.scss'

interface IStepRendererProps {
  pid: string
  steps: (IWizardStep | IWizardStepsGroup)[]
  activeId: Nullable<string>

  widgetParams: IObjectAny
  flexWidth: FlexDirectionBreak
  metrics: MeasureController
}

export const StepRenderer: FC<IStepRendererProps> = ({
  steps,
  activeId,
  widgetParams,
  flexWidth,
  metrics,
  pid,
}) => {
  /** ------------------------- Selectors ------------------------- */

  const fYear = useSelector(selectFinancialYear)
  const partnerId = useSelector(selectPartnerId)
  const programType = useSelector(selectProgramType)
  const isEditMode = useSelector(selectPublishingMode)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()

  const { updatePlanningStepApplicable } = useNotApplicablePlanningStep({
    changeLoading: (isLoading: boolean) => {
      dispatch(setWizardIsLoading(isLoading))
    },
    partnerId,
    fYear,
  })

  /** ------------------------- Variables ------------------------- */

  const flattenedSteps = useMemo<IWizardStep[]>(() => {
    return steps.reduce<IWizardStep[]>((acc, item) => {
      if (isWizardStepsGroup(item)) {
        acc.push(...item.steps)
      } else {
        acc.push(item)
      }
      return acc
    }, [])
  }, [steps])

  const activeStep = useMemo(() => {
    return flattenedSteps.find((el) => el.id === activeId)
  }, [activeId, flattenedSteps])

  const selectedFinSobItem = useMemo(() => {
    return {
      ...defaultFinSobItem,

      FYear: fYear,
      IsApplicable: activeStep?.isApplicable ?? 1,
      IsApplicableDisabled: activeStep?.isApplicableDisabled ?? 0,
      ProgramType: (activeStep?.programType ?? programType) as ProgramTypes,
      Title: activeStep?.category ?? '',
    }
  }, [
    activeStep?.category,
    activeStep?.isApplicable,
    activeStep?.isApplicableDisabled,
    activeStep?.programType,
    fYear,
    programType,
  ])

  /** ------------------------- Render methods ------------------------- */
  const renderStepComponent = useCallback(
    (widgetId: string): React.ReactNode => {
      switch (widgetId) {
        case 'ramManagerAssignment':
          return <RamManagerAssignment metrics={metrics} partnerId={pid} />
        case 'basicInfo':
          return (
            <PartnerBasicInfo widgetParams={widgetParams} metrics={metrics} pid={pid} isEmbeded />
          )
        case 'contacts':
          return (
            <ExtendedPartnerContacts
              partnerId={partnerId}
              programType={programType}
              metrics={metrics}
            />
          )
        case 'planning':
          return (
            <Planning
              widgetParams={widgetParams}
              metrics={metrics}
              pid={pid}
              selectedFinSobItem={selectedFinSobItem}
              isEmbeded
            />
          )
        case 'assortment':
          return (
            <AssortmentTab
              isEmbedded
              partnerId={partnerId}
              fYear={fYear}
              programType={(activeStep?.programType ?? programType) as ProgramTypes}
              category={activeStep?.category}
              isApplicableCategory={activeStep?.isApplicable}
            />
          )
        case 'acknowledge':
          return (
            <CommitmentWrapper
              widgetParams={widgetParams}
              flexWidth={flexWidth}
              metrics={metrics}
              pid={pid}
              programType={(activeStep?.programType ?? programType) as ProgramTypes}
            />
          )
        default:
          return null
      }
    },
    [
      widgetParams,
      metrics,
      pid,
      flexWidth,
      selectedFinSobItem,
      partnerId,
      fYear,
      activeStep?.programType,
      programType,
      activeStep?.category,
      activeStep?.isApplicable,
    ]
  )

  /** ------------------------- Render ------------------------- */

  if (!activeStep) {
    return <>Please select a wizard step</>
  }

  if (!activeStep.isApplicable) {
    return (
      <NotApplicableStep
        isEditMode={isEditMode}
        activeStep={activeStep}
        fYear={fYear}
        partnerId={partnerId}
        programType={programType}
        onChangeStepApplicable={updatePlanningStepApplicable}
      />
    )
  }

  return <div className={styles.stepRenderer}>{renderStepComponent(activeStep.widgetId)}</div>
}
